/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/**
 * The part of a vdom node this module reads.
 */
export interface VdomElement {
	attrs?: { className?: unknown; [key: string]: unknown };
	children?: VdomChild | VdomChildren;
}

/**
 * A single entry of a vdom `children` array: a vdom node, a primitive, or nothing.
 */
export type VdomChild = VdomElement | string | number | boolean | null | undefined;

/**
 * A vdom `children` array.
 */
export type VdomChildren = VdomChild[];

/**
 * What the helpers below accept: either a vdom node or a bare children array. Core's `view()`
 * methods return both shapes.
 */
export type VdomParent = VdomChild | VdomChildren;

/**
 * Whether a child is the object form — the only one that can carry `attrs`/`children`.
 */
function isVdomElement(child: VdomParent): child is VdomElement {
	return typeof child === 'object' && child !== null && !Array.isArray(child);
}

/**
 * Searches for a child element in a vdom element (object returned by
 * the `view()` method of a `Component`, or a child of this original vdom element).
 *
 * @param parent vdom element to search in
 * @param childClass Class name of the child element that needs to be found
 * @param recursive Whether the search should be done recursively
 *
 * @returns The child element that has been found, or `undefined`
 */
export function findChild(
	parent: VdomParent,
	childClass: string,
	recursive: boolean = false,
	maxDepth: number = 50,
	depth: number = 0
): VdomElement | undefined {
	if (!childClass) {
		throw Error('A child class needs to be passed as second argument of the findChild function');
	}
	const children = getChildren(parent);
	const child = children.find((child): child is VdomElement => {
		const child_className = (isVdomElement(child) ? child.attrs?.className : undefined) ?? '';
		return typeof child_className === 'string' && child_className.includes(childClass);
	});

	// Recursive search
	if (recursive && !child && depth < maxDepth) {
		for (let sub_parent of children) {
			const sub_child = findChild(sub_parent, childClass, true, maxDepth, depth + 1);
			if (sub_child) {
				return sub_child;
			}
		}
	}

	return child;
}

/**
 * Searches for a child element in a vdom element and returns its index in the `children` array
 *
 * @param parent vdom element to search in
 * @param childClass Class name of the child element that needs to be found
 *
 * @returns The index of the child element that has been found or `false` if not found
 */
export function childIndex(parent: VdomParent, childClass: string): number | false {
	const index = getChildren(parent).indexOf(findChild(parent, childClass));

	// If not found, return false
	if (index === -1) {
		return false;
	}

	return index;
}

/**
 * Get the children of an vdom element. If there are no children, return empty array.
 *
 * @param parent vdom element to search in
 *
 * @returns the children
 */
export function getChildren(parent: VdomParent): VdomChildren {
	if (Array.isArray(parent)) {
		return parent;
	}
	const children = typeof parent === 'object' && parent !== null ? parent.children : undefined;
	if (!Array.isArray(children)) {
		return [];
	}
	return children;
}
